/**
 * Gestão de Pedidos — peças do detalhe (`/pedidos/:orderId`, §3.2 da spec +
 * decisão 4 da rodada 2, 2026-09-23).
 *
 * Abas: Venda | Mensagens | Pós-venda | Comprador | Financeiro | JSON raw
 * (só `developer`) — a aba Comprador (histórico do mesmo buyer_id) entrou na
 * rodada 5 de 2026-09-29 (`./compradorTab.jsx`), com a triagem do caso
 * (`./caseReview.jsx`) e o status do financeiro (`./financeReview.jsx`).
 * O Histórico deixou de ser aba na rodada 3 (2026-09-23) e
 * virou a coluna fixa `HistoryRail` (`./historyRail.jsx`) ao lado das abas.
 * Cada aba é um componente com estado PRÓPRIO que
 * carrega sob demanda ao abrir e FICA MONTADA (`LazyPane`) enquanto o pedido
 * for o mesmo — voltar para uma aba não refaz requests. A página remonta as
 * abas por `key={orderId}`, então trocar de pedido zera tudo sem guarda
 * extra. Dentro de cada aba, `seq`/`alive` descartam a resposta atrasada.
 *
 * Linguagem visual: reta e plana (cantos QUADRADOS), negrito SÓ na identidade
 * e na ação primária, cor com significado (sky = Atendimento/ML, amber =
 * Logística, cyan = Financeiro, red = urgente).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowClockwise,
    ArrowSquareOut,
    CaretDown,
    CaretRight,
    ClipboardText,
    CurrencyDollar,
    EnvelopeSimple,
    User,
    WarningCircle,
    Wrench,
} from 'shared/icons';
import { ConversationThread, MESSAGE_MAX_LENGTH, ReplyComposer } from '../../components/atendimento/atendimentoShared';
import PaymentOrderReport from '../../components/payments/PaymentOrderReport';
import { CaseLossRequest } from '../../components/pedidos/CaseBlingFiscal';
import {
    CaseFiscalAction,
    CleanIdentityValue,
    CleanPackId,
    MlClaimMessagesUrl,
    OCCURRENCE_KIND_LABELS,
    OccurrenceTitle,
    OrderStatusPill,
    OriginBadge,
} from '../../components/pedidos/pedidosShared';
import {
    CaseDueInfo,
    ComputeTracks,
    Fact,
    fmtDate,
    HasPhysicalFlow,
    MediationChat,
    Panel,
    REFUND_LABELS,
    RecontactOurTurn,
    StatusPill,
} from '../../components/rma/rmaShared';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
    ML_CLAIM_STAGE_LABELS,
    ML_LOGISTIC_TYPE_LABELS,
    ML_SHIPPING_STATUS_LABELS,
    MlLabel,
    MP_MONEY_RELEASE_LABELS,
} from '../../constants/mlClaimCatalog';
import { LIST_HEALTH_LABELS } from '../../constants/paymentHealth';
import {
    ConversationStateMeta,
    HasMlReturnInvoice,
    IsCaseClosed,
    IsReturnSkipped,
    IsReturnToUs,
    NeedsCaseReconcile,
    NeedsLossNf,
    NeedsReturnNfe,
    TONE_CLASSES,
    TurnMeta,
} from '../../constants/pedidoTags';
import api, { GetToken } from '../../services/api';
import { ResolveUploadsBaseUrl } from '../../utils/assetUrl';
import { SortChronological } from '../../utils/chatOrder';
import { FormatInt, FormatMoney } from '../../utils/format';
import RmaDetail from '../RmaDetail';
import { CaseReviewPanel, FinanceSupportCasesPanel } from './caseReview';
import { FinanceReviewPanel } from './financeReview';

export { OCCURRENCE_KIND_LABELS };

// ── Abas ─────────────────────────────────────────────────────────────────────

/** `developer: true` = a aba só existe para quem tem a permissão `developer`. */
export const DETAIL_TABS = [
    { key: 'venda', label: 'Venda', Icon: ClipboardText },
    { key: 'mensagens', label: 'Mensagens', Icon: EnvelopeSimple },
    { key: 'posvenda', label: 'Pós-venda', Icon: WarningCircle },
    // Rodada 5 (2026-09-29): histórico do mesmo comprador (buyer_id).
    { key: 'comprador', label: 'Comprador', Icon: User },
    { key: 'financeiro', label: 'Financeiro', Icon: CurrencyDollar },
    { key: 'payloads', label: 'JSON raw', Icon: Wrench, developer: true },
];

export const DETAIL_TAB_KEYS = DETAIL_TABS.map((tab) => tab.key);

/**
 * Keys antigas que ainda podem estar em links salvos (`?tab=`): as da rodada
 * 1 e a aba Histórico (rodada 2), que virou coluna fixa — cai na Venda.
 */
const TAB_ALIASES = { resumo: 'venda', ocorrencias: 'posvenda', historico: 'venda' };

/** Aba efetiva a partir do `?tab=` — alias antigo resolvido, inválida vira Venda. */
export function ResolveDetailTab(param, { developer = false } = {}) {
    const key = TAB_ALIASES[param] || param;
    const tab = DETAIL_TABS.find((item) => item.key === key);
    if (!tab || (tab.developer && !developer)) {
        return 'venda';
    }
    return tab.key;
}

const SUB_TONES = {
    danger: 'font-semibold text-danger-600 dark:text-danger-400',
    warn: 'font-semibold text-warning-text dark:text-warning-300',
    ok: 'text-emerald-600 dark:text-emerald-400',
    neutral: 'text-mono-400 dark:text-mono-500',
};

const TAB_GRID_COLUMNS = {
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6',
};

/**
 * Menu de blocos das abas — mesmo desenho do `menuTabs` de `pages/Rmas.jsx`
 * (grid `gap-px`, ativo = borda superior preta 3px). `tabs[].sub` é a linha
 * de contexto (ex.: "3 em andamento") com `tone` danger|warn|ok|neutral.
 */
export const TabBlocks = ({ tabs, active, onChange }) => (
    <div
        className={`grid grid-cols-2 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800 sm:grid-cols-3 ${
            TAB_GRID_COLUMNS[tabs.length] || 'lg:grid-cols-4'
        }`}
    >
        {tabs.map((tab) => {
            const isActive = active === tab.key;
            const TabIcon = tab.Icon;
            return (
                <button
                    key={tab.key}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onChange(tab.key)}
                    className={`flex flex-col gap-1 border-t-[3px] px-4 py-2.5 text-left transition-colors ${
                        isActive
                            ? 'border-black bg-white dark:border-white dark:bg-mono-950'
                            : 'border-transparent bg-white hover:bg-mono-50 dark:bg-mono-950 dark:hover:bg-mono-900'
                    }`}
                >
                    <span
                        className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
                            isActive ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                        }`}
                    >
                        <TabIcon size={13} /> {tab.label}
                    </span>
                    <span className={`truncate text-[11px] ${SUB_TONES[tab.sub?.tone] || SUB_TONES.neutral}`}>
                        {tab.sub?.text || ''}
                    </span>
                </button>
            );
        })}
    </div>
);

/**
 * Painel de aba que monta na PRIMEIRA visita e depois só se esconde: o estado
 * e o cache de cada aba sobrevivem à troca de aba (revisão adversarial da
 * rodada 2). O `key={orderId}` de quem embute zera tudo ao trocar de pedido.
 */
export const LazyPane = ({ active, children }) => {
    const [mounted, setMounted] = useState(Boolean(active));
    useEffect(() => {
        if (active) {
            setMounted(true);
        }
    }, [active]);
    if (!mounted) {
        return null;
    }
    return <div hidden={!active}>{children}</div>;
};

// ── Helpers de wire ──────────────────────────────────────────────────────────

/**
 * A ocorrência do detalhe de pedidos é um SUBCONJUNTO da linha do RMA
 * (`return_status` = `return_shipment_status`, sem `source`/`return_id`).
 * Adapta o mínimo para `ComputeTracks`/`SituationLine` não lerem errado
 * enquanto a linha completa (`/rmas?q=`) não chega — depois ela só COMPLETA
 * o que falta (`MergeOccurrenceRow`).
 */
export function AdaptOccurrence(occ) {
    const kinds = Array.isArray(occ?.kinds) ? occ.kinds : occ?.kind ? [occ.kind] : [];
    const hasReturn = Boolean(occ?.has_return) || kinds.includes('devolucao');
    return {
        ...occ,
        kinds,
        source: occ?.source || 'ML',
        return_id: occ?.return_id ?? (hasReturn ? 'devolucao' : null),
        return_shipment_status: occ?.return_shipment_status ?? occ?.return_status ?? null,
    };
}

/**
 * Linha do caso na Pós-venda (F07 da revisão): o DETALHE do pedido é o dado
 * mais novo — cada campo que ele traz GANHA, inclusive `null` (status,
 * prazo, responsável, vez, reembolso…); a linha completa do `/rmas?q=` só
 * completa o que o detalhe não traz (`ml_pending_actions`, `tracking_number`,
 * SLAs…). Os três campos que `AdaptOccurrence` CHUTA (`source`, `return_id`,
 * `return_shipment_status`) ficam com o valor real da linha completa quando
 * o detalhe não os manda. Exceção: o dinheiro do caso (`MASKED_FINANCE_KEYS`)
 * vem `null` no detalhe para quem não vê o setor Financeiro — esse `null` é
 * máscara, não dado, e não apaga o valor real da linha completa.
 */
const MASKED_FINANCE_KEYS = ['refund_amount', 'refund_channel', 'refund_reconciled_at', 'nf_loss_at'];

export function MergeOccurrenceRow(occ, full = null) {
    const adapted = AdaptOccurrence(occ);
    if (!full) {
        return adapted;
    }
    const own = (key) => Object.hasOwn(occ, key) && occ[key] !== undefined;
    const fromDetail = Object.fromEntries(Object.entries(occ).filter(([, value]) => value !== undefined));
    let returnShipmentStatus = full.return_shipment_status ?? null;
    if (own('return_shipment_status')) {
        returnShipmentStatus = occ.return_shipment_status;
    } else if (own('return_status')) {
        returnShipmentStatus = occ.return_status;
    }
    const finance = Object.fromEntries(MASKED_FINANCE_KEYS.map((key) => [key, occ[key] ?? full[key] ?? null]));
    return {
        ...full,
        ...fromDetail,
        ...finance,
        kinds: adapted.kinds,
        source: own('source') ? occ.source : (full.source ?? adapted.source),
        return_id: own('return_id') ? occ.return_id : (full.return_id ?? adapted.return_id),
        return_shipment_status: returnShipmentStatus,
    };
}

/**
 * Etiqueta do caso quando o wire ainda não traz `has_label`: as tags do
 * PEDIDO dizem se a devolução física pendente tem etiqueta — "Devolução para
 * inspecionar" (Atendimento) só com etiqueta, "Devolução para etiquetar"
 * (Logística) só sem. Vale apenas quando há UM caso físico não inspecionado
 * no pedido (a tag só pode ser dele) e só uma das duas tags acende; senão
 * fica como veio (sem `has_label`, o trilho segue as etapas de antes).
 */
export function ApplyOrderLabelHint(rows = [], orderTagKeys = []) {
    const keys = new Set(orderTagKeys);
    const toInspect = keys.has('devolucao_para_inspecionar');
    const toLabel = keys.has('devolucao_para_etiquetar');
    if (toInspect === toLabel) {
        return rows;
    }
    const pending = (row) =>
        Boolean(row.return_id) && HasPhysicalFlow(row) && ['ABERTO', 'EM_TRANSITO', 'RECEBIDO'].includes(row.status);
    const candidates = rows.filter(pending);
    if (candidates.length !== 1 || typeof candidates[0].has_label === 'boolean') {
        return rows;
    }
    return rows.map((row) => (row === candidates[0] ? { ...row, has_label: toInspect } : row));
}

/**
 * Assinatura das ocorrências do detalhe (`id:status:updated_at`): muda quando
 * o pedido recarregado traz um caso novo ou alterado — é ela, não a contagem,
 * que dispara a nova busca da linha completa. Sem casos → ''.
 */
export function OccurrenceSignature(ocorrencias = []) {
    return (Array.isArray(ocorrencias) ? ocorrencias : [])
        .map((occ) => `${occ?.id}:${occ?.status ?? ''}:${occ?.updated_at ?? ''}`)
        .join('|');
}

// Ordem de chat (mais antiga em cima): `SortChronological` mora em
// `utils/chatOrder.js` (o `RmaDetail` embutido usa o mesmo) — re-exportado
// aqui para quem já importava deste módulo.
export { SortChronological };

/**
 * Estado da conversa: o backend manda `conversation.state` (decisão 1). Sem
 * ele (backend antigo), deriva do espelho local com a MESMA regra.
 */
export function ConversationState(conversation) {
    if (!conversation) {
        return null;
    }
    if (conversation.state) {
        return conversation.state;
    }
    if (conversation.conversation_substatus === 'blocked_by_claim') {
        return 'bloqueada_por_reclamacao';
    }
    if (conversation.conversation_status === 'BLOCKED') {
        return 'bloqueada';
    }
    if (!Number(conversation.messages_count || 0)) {
        return 'sem_mensagens';
    }
    return conversation.last_message_from === 'seller' ? 'aguardando_comprador' : 'falta_nossa_resposta';
}

const OPEN_OCCURRENCE_STATUSES = new Set(['ABERTO', 'EM_TRANSITO', 'RECEBIDO', 'INSPECIONADO']);

/**
 * O veredito da devolução (return_review_*) já pode ser dado? Só depois da
 * CHEGADA ao nosso CD (dono, 2026-09-23 — mesma regra do `arrivedOurs` da
 * fila revisar_devolucao: entregue vindo para nós, RECEBIDO/INSPECIONADO ou
 * etiquetada) ou quando a devolução não vem para nós (Full/armazém do ML —
 * não há chegada a esperar). Em trânsito para cá não é a nossa vez.
 */
function reviewArrived(occ) {
    if (!IsReturnToUs(occ)) {
        return true;
    }
    const shipmentStatus = occ.return_shipment_status ?? occ.return_status ?? null;
    return (
        ['RECEBIDO', 'INSPECIONADO'].includes(occ.status) || occ.has_label === true || shipmentStatus === 'delivered'
    );
}

/**
 * "Vez de:" da reclamação/mediação: o backend manda `turn` (F03 — ação
 * obrigatória/veredito, `action_responsible`, ninguém pode agir → ML) e o
 * `null` dele VALE ("sem vez"); só quando o campo nem vem (backend antigo)
 * deriva de `mediation_responsible` (respondent → nossa, complainant →
 * comprador) e, aberta sem responsável, é processo interno do ML.
 */
export function OccurrenceTurn(occ) {
    if (occ && Object.hasOwn(occ, 'turn')) {
        return occ.turn ?? null;
    }
    // Recontato sem `turn` no wire: a mesma regra do chip e do trilho
    // (`RecontactOurTurn`, fila recontatos_responder) — nossa só com
    // respondent + claim aberto + prazo por vencer (ou veredito da
    // devolução pendente); senão o ML analisa.
    if (occ?.mediation_status === 'RECONTATO') {
        // Veredito pendente só conta depois da chegada (`reviewArrived`).
        const reviewPending =
            (Array.isArray(occ.ml_pending_actions) ? occ.ml_pending_actions : []).some((action) =>
                String(action).startsWith('return_review'),
            ) && reviewArrived(occ);
        if (RecontactOurTurn(occ) || reviewPending) {
            return 'nossa';
        }
        return occ.ml_claim_status === 'closed' ? null : 'ml';
    }
    if (occ?.mediation_responsible === 'respondent') {
        return 'nossa';
    }
    if (occ?.mediation_responsible === 'complainant') {
        return 'comprador';
    }
    const open = occ?.ml_claim_status ? occ.ml_claim_status !== 'closed' : OPEN_OCCURRENCE_STATUSES.has(occ?.status);
    return open ? 'ml' : null;
}

/** Foto/anexo que o ML colocou na reclamação — mesmo proxy do `RmaDetail`. */
function claimAttachmentUrl(rmaId, filename) {
    const token = GetToken();
    return `${ResolveUploadsBaseUrl()}/rmas/${rmaId}/claim-attachments/${encodeURIComponent(filename)}${
        token ? `?access_token=${token}` : ''
    }`;
}

// ── Pílula de saúde da conciliação (payments_health.status) ──────────────────

const HEALTH_PILL = {
    ok: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
    attention:
        'bg-danger-50 text-danger-700 ring-danger-600/20 dark:bg-danger-400/10 dark:text-danger-300 dark:ring-danger-400/30',
    pending: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
};

export const HealthPill = ({ status, size = 'sm' }) => {
    const pad = size === 'md' ? 'px-3 py-1 text-[12px]' : 'px-2.5 py-0.5 text-[11px]';
    return (
        <span
            className={`inline-flex items-center whitespace-nowrap font-semibold ring-1 ring-inset ${pad} ${
                HEALTH_PILL[status] || HEALTH_PILL.pending
            }`}
        >
            {LIST_HEALTH_LABELS[status] || status || '—'}
        </span>
    );
};

const LINK_CLASS =
    'inline-flex items-center gap-1 text-sm text-sky-700 underline-offset-2 hover:underline dark:text-sky-300';

/** Aviso plano (borda esquerda com o tom) usado nas abas. */
const Notice = ({ tone = 'neutral', children }) => {
    const classes = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
    return (
        <div className={`border-l-4 bg-mono-50 px-3 py-2 text-sm dark:bg-mono-900/40 ${classes.border}`}>
            {children}
        </div>
    );
};

// ── Aba Venda (ex-Resumo) ────────────────────────────────────────────────────

export const VendaTab = ({ order, packOrders = [], backTo = null }) => {
    const shipping = order.shipping || {};
    const buyer = order.buyer || {};
    const items = Array.isArray(order.items) ? order.items : [];
    const place = [shipping.city, shipping.uf].filter(Boolean).join(' / ');
    const linkState = backTo ? { from: backTo } : undefined;
    const packId = CleanPackId(order.pack_id);
    return (
        <div className="flex min-w-0 flex-col gap-4">
            {/* Pack acima da Venda (pedido do dono, 24/09): quando é pedido único, uma
                linha discreta em vez de um painel inteiro — era o que quebrava o layout.
                Com irmãos no pack, o painel com a lista continua. */}
            {!packId || packOrders.length === 0 ? (
                <p className="text-[11px] text-mono-400 dark:text-mono-600">
                    {!packId ? (
                        'Pedido sem pack.'
                    ) : (
                        <>
                            Único pedido do pack <span className="font-mono">{packId}</span>.
                        </>
                    )}
                </p>
            ) : (
                <Panel title="Pack">
                    <ul className="flex flex-col divide-y divide-mono-100 dark:divide-mono-900">
                        {packOrders.map((po) => (
                            <li key={po.external_order_id}>
                                <Link
                                    to={`/pedidos/${po.external_order_id}`}
                                    state={linkState}
                                    className="flex flex-col gap-0.5 py-1.5 transition-colors hover:bg-mono-50 dark:hover:bg-mono-900/40"
                                >
                                    <span className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-[13px] font-bold text-black dark:text-white">
                                            #{po.external_order_id}
                                        </span>
                                        <OrderStatusPill status={po.status} />
                                    </span>
                                    <span className="flex items-center justify-between gap-2 text-[11px] text-mono-500 dark:text-mono-400">
                                        <span className="tabular-nums">{FormatMoney(po.total_amount)}</span>
                                        <span>{fmtDate(po.date_created)}</span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Panel>
            )}
            <Panel
                title={
                    <>
                        Venda <OriginBadge origin="ml" />
                    </>
                }
            >
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                    <Fact label="Situação">
                        <OrderStatusPill status={order.status} />
                    </Fact>
                    <Fact label="Criado em">{fmtDate(order.date_created)}</Fact>
                    <Fact label="Atualizado em">{fmtDate(order.date_last_updated)}</Fact>
                    <Fact label="Total">
                        <span className="tabular-nums">{FormatMoney(order.totals?.total_amount)}</span>
                    </Fact>
                    <Fact label="Comprador">{buyer.name || buyer.nickname}</Fact>
                    <Fact label="Apelido ML" mono>
                        {buyer.nickname}
                    </Fact>
                    <Fact label="ID do comprador" mono>
                        {buyer.buyer_id}
                    </Fact>
                    <Fact label="Frete vendedor / comprador">
                        <span className="tabular-nums">
                            {FormatMoney(order.shipping_cost_seller)} / {FormatMoney(order.shipping_cost_buyer)}
                        </span>
                    </Fact>
                </div>
            </Panel>

            <Panel title={`Itens (${items.length})`}>
                {items.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">Sem itens visíveis nesta venda.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[520px] text-sm">
                            <thead>
                                <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                                    <th className="py-1 pr-3">SKU</th>
                                    <th className="pr-3">Produto</th>
                                    <th className="pr-3 text-right">Qtd</th>
                                    <th className="pr-3 text-right">Unitário</th>
                                    <th className="text-right">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr
                                        key={`${item.item_id || ''}|${item.sku || ''}`}
                                        className="border-t border-mono-100 dark:border-mono-900"
                                    >
                                        <td className="py-1.5 pr-3 font-mono text-[12px] text-mono-700 dark:text-mono-200">
                                            {item.sku || '—'}
                                        </td>
                                        <td className="pr-3 text-mono-800 dark:text-mono-100">
                                            {item.title || item.item_id || '—'}
                                            {item.item_id ? (
                                                <span className="ml-1.5 font-mono text-[11px] text-mono-400 dark:text-mono-500">
                                                    {item.item_id}
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="pr-3 text-right tabular-nums">{FormatInt(item.quantity)}</td>
                                        <td className="pr-3 text-right tabular-nums">{FormatMoney(item.unit_price)}</td>
                                        <td className="text-right tabular-nums">
                                            {FormatMoney(Number(item.quantity || 0) * Number(item.unit_price || 0))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            <Panel
                title={
                    <>
                        Envio <OriginBadge origin="ml" />
                    </>
                }
            >
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                    <Fact label="Status">{MlLabel(ML_SHIPPING_STATUS_LABELS, shipping.status)}</Fact>
                    <Fact label="Substatus">{shipping.substatus}</Fact>
                    <Fact label="Logística">{MlLabel(ML_LOGISTIC_TYPE_LABELS, shipping.logistic_type)}</Fact>
                    <Fact label="Shipment" mono>
                        {shipping.shipment_id}
                    </Fact>
                    <Fact label="Cidade / UF">{place}</Fact>
                </div>
            </Panel>
        </div>
    );
};

/** Nome da rodada 1 — mantido para quem ainda importar. */
export const ResumoTab = VendaTab;

// ── Aba Mensagens ────────────────────────────────────────────────────────────

/**
 * Conversa pós-venda do pack, FIEL AO ML (decisão 1 da rodada 2): o backend
 * já hidratou a conversa ao montar o detalhe e manda `conversation.state`.
 *   null                     → "Sem conversa no ML" + "Verificar de novo"
 *                              (refaz o GET do detalhe; NUNCA "Abrir conversa")
 *   bloqueada_por_reclamacao → sem compositor; aviso + atalho da mediação no
 *                              ML + as mensagens da reclamação (via RMA)
 *   bloqueada                → sem compositor (bloqueio por outro motivo)
 *   falta_nossa_resposta     → thread + compositor em destaque
 *   aguardando_comprador     → thread + compositor (pode responder de novo)
 *   sem_mensagens            → thread vazia + compositor
 * Mesmas guardas de `pages/Atendimento.jsx`: `seq` descarta a resposta
 * atrasada; envio/sugestão só aplicam se a conversa carregada for a mesma.
 */
export const MensagensTab = ({
    orderId,
    packId = null,
    conversation = null,
    ocorrencias = [],
    canRead = false,
    canReply = false,
    toast,
    onConversationChanged = null,
    onRecheck = null,
    rechecking = false,
}) => {
    const [detail, setDetail] = useState(null); // { conversation, messages, product_keys }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [aiMeta, setAiMeta] = useState({ enabled: false, provider: null });
    const [quickReplies, setQuickReplies] = useState([]);
    const [claimMessages, setClaimMessages] = useState({ rmaId: null, messages: null, loading: false });
    const seqRef = useRef(0);
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const conversationId = conversation?.id || null;
    const messages = useMemo(() => SortChronological(detail?.messages, 'message_date') || [], [detail?.messages]);
    const claimThread = useMemo(
        () => SortChronological(claimMessages.messages, 'date_created'),
        [claimMessages.messages],
    );
    const state = ConversationState(conversation);
    const stateMeta = ConversationStateMeta(state);
    const blockedByClaim = state === 'bloqueada_por_reclamacao';
    const blocked = blockedByClaim || state === 'bloqueada';
    const pack = CleanPackId(packId);

    // Reclamação que bloqueia a mensageria: casa `claim_ids` (números) com a
    // ocorrência espelhada para buscar as mensagens dela; sem ocorrência, o
    // primeiro id ainda serve para o atalho do ML.
    const claimIds = (Array.isArray(conversation?.claim_ids) ? conversation.claim_ids : [])
        .map((id) => CleanIdentityValue(id))
        .filter(Boolean);
    const blockingCase =
        (Array.isArray(ocorrencias) ? ocorrencias : []).find((occ) =>
            claimIds.includes(CleanIdentityValue(occ?.external_claim_id)),
        ) || null;
    const blockingClaimId = CleanIdentityValue(blockingCase?.external_claim_id) || claimIds[0] || null;
    const blockingRmaId = blockedByClaim ? blockingCase?.id || null : null;

    const loadConversation = useCallback(async (id) => {
        const seq = ++seqRef.current;
        setLoading(true);
        setError('');
        try {
            const data = await api.getAtendimentoConversation(id);
            if (seqRef.current !== seq) {
                return;
            }
            setDetail(data);
            setDraft(data?.conversation?.ai_suggestion || '');
        } catch (loadError) {
            if (seqRef.current !== seq) {
                return;
            }
            setError(loadError.message || 'Falha ao abrir a conversa');
        } finally {
            if (seqRef.current === seq) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!conversationId || !canRead) {
            setDetail(null);
            return;
        }
        loadConversation(conversationId);
    }, [conversationId, canRead, loadConversation]);

    // Mensagens da reclamação que bloqueia (endpoint do RMA, já existente).
    useEffect(() => {
        if (!blockingRmaId || !canRead) {
            setClaimMessages({ rmaId: null, messages: null, loading: false });
            return undefined;
        }
        let alive = true;
        setClaimMessages({ rmaId: blockingRmaId, messages: null, loading: true });
        api.getRmaMediationMessages(blockingRmaId)
            .then((data) => {
                if (alive) {
                    setClaimMessages({
                        rmaId: blockingRmaId,
                        messages: Array.isArray(data?.messages) ? data.messages : [],
                        loading: false,
                    });
                }
            })
            .catch(() => {
                if (alive) {
                    setClaimMessages({ rmaId: blockingRmaId, messages: [], loading: false });
                }
            });
        return () => {
            alive = false;
        };
    }, [blockingRmaId, canRead]);

    // IA (habilitada?) e respostas rápidas — uma vez por montagem.
    useEffect(() => {
        if (!canRead) {
            return undefined;
        }
        let alive = true;
        api.getAtendimentoSummary()
            .then((summary) => {
                if (alive) {
                    setAiMeta({ enabled: Boolean(summary?.ai_enabled), provider: summary?.ai_provider || null });
                }
            })
            .catch(() => {});
        api.getAtendimentoQuickReplies()
            .then((data) => {
                if (alive) {
                    setQuickReplies(Array.isArray(data?.results) ? data.results : []);
                }
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [canRead]);

    const handleSend = async () => {
        const id = detail?.conversation?.id;
        const text = draft.trim();
        if (!id || !text) {
            return;
        }
        const seq = seqRef.current;
        setSending(true);
        try {
            const data = await api.sendAtendimentoMessage(id, text);
            if (seqRef.current !== seq) {
                return;
            }
            setDetail(data);
            setDraft('');
            toastRef.current?.success('Mensagem enviada.');
            // Enviou = a bola passou ao comprador; o `state` do detalhe é
            // recalculado só no próximo GET, então a página recebe o estado
            // já correto junto com o espelho devolvido pelo envio.
            onConversationChanged?.({
                ...(data?.conversation || {}),
                needs_reply: false,
                last_message_from: 'seller',
                state: 'aguardando_comprador',
            });
        } catch (sendError) {
            toastRef.current?.error(sendError.message || 'Falha ao enviar');
        } finally {
            if (seqRef.current === seq) {
                setSending(false);
            }
        }
    };

    const handleSuggest = async () => {
        const id = detail?.conversation?.id;
        if (!id) {
            return;
        }
        const seq = seqRef.current;
        setSuggesting(true);
        try {
            const updated = await api.suggestAtendimentoConversation(id);
            if (seqRef.current !== seq) {
                return;
            }
            setDetail((current) => (current ? { ...current, conversation: updated } : current));
            setDraft(updated?.ai_suggestion || '');
        } catch (suggestError) {
            toastRef.current?.error(suggestError.message || 'Falha ao gerar sugestão');
        } finally {
            if (seqRef.current === seq) {
                setSuggesting(false);
            }
        }
    };

    const pasteQuickReply = (body) => {
        setDraft((current) => (current && current.trim() ? `${current}\n${body}` : body));
    };

    if (!canRead) {
        return (
            <Panel title="Mensagens">
                <p className="text-sm text-mono-500 dark:text-mono-400">
                    Sem permissão para ler conversas pós-venda (atendimento:read).
                </p>
            </Panel>
        );
    }

    const hydrateNotice = conversation?.hydrate_error ? (
        <Notice tone="amber">
            Não foi possível consultar o Mercado Livre agora — mostrando o espelho local
            {conversation?.hydrated_at ? ` (atualizado ${fmtDate(conversation.hydrated_at)})` : ''}.
        </Notice>
    ) : null;

    if (!conversationId) {
        return (
            <Panel title="Mensagens">
                {hydrateNotice}
                <p className="text-sm text-mono-500 dark:text-mono-400">Sem conversa no ML para este pedido.</p>
                {onRecheck ? (
                    <div>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onRecheck()}
                            disabled={rechecking}
                            loading={rechecking}
                        >
                            <ArrowClockwise size={14} /> Verificar de novo
                        </Button>
                        <p className="mt-1 text-[11px] text-mono-400 dark:text-mono-500">
                            Consulta o Mercado Livre outra vez pelo {pack ? `pack ${pack}` : `pedido ${orderId}`}.
                        </p>
                    </div>
                ) : null}
            </Panel>
        );
    }

    if (loading && !detail) {
        return <LoadingSpinner className="py-16" />;
    }

    if (error && !detail) {
        return (
            <Panel title="Mensagens">
                {hydrateNotice}
                <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
                <div>
                    <Button size="sm" variant="secondary" onClick={() => loadConversation(conversationId)}>
                        Tentar de novo
                    </Button>
                </div>
            </Panel>
        );
    }

    if (!detail) {
        return null;
    }

    const conv = detail.conversation || {};
    const messagesCount = conversation?.messages_count ?? conv.messages_count ?? messages.length;
    const lastFrom = conversation?.last_message_from ?? conv.last_message_from;
    const lastAt = conversation?.last_message_at ?? conv.last_message_at;
    // "Pack" só quando o PEDIDO tem pack (o espelho usa o nº do pedido como
    // pack_id quando não há pack — não é um pack de verdade).
    const headline = [pack ? `Pack ${pack}` : null, conv.buyer_nickname || null, conv.sku ? `SKU ${conv.sku}` : null]
        .filter(Boolean)
        .join(' · ');
    const stateClasses = TONE_CLASSES[stateMeta.tone] || TONE_CLASSES.neutral;

    const composer = canReply ? (
        <ReplyComposer
            value={draft}
            onChange={setDraft}
            maxLength={MESSAGE_MAX_LENGTH}
            rows={3}
            placeholder="Escreva a mensagem ou gere uma sugestão com IA..."
            quickReplies={quickReplies}
            onPickQuickReply={pasteQuickReply}
            aiEnabled={aiMeta.enabled}
            aiProvider={aiMeta.provider}
            suggesting={suggesting}
            onSuggest={handleSuggest}
            suggestionAt={conv.ai_suggestion_at}
            sending={sending}
            onSend={handleSend}
        />
    ) : (
        <p className="text-[11px] text-mono-400 dark:text-mono-500">Responder exige a permissão atendimento:reply.</p>
    );

    return (
        <div className="flex flex-col gap-4">
            <Panel
                title={
                    <span className={`inline-flex items-center gap-1.5 ${stateClasses.text}`}>
                        <span className={`h-2 w-2 ${stateClasses.dot}`} />
                        {stateMeta.label}
                        <OriginBadge origin="ml" />
                    </span>
                }
                className="min-w-0"
                action={
                    <span className="text-[11px] text-mono-500 dark:text-mono-400">
                        {FormatInt(messagesCount ?? 0)} mensagens
                        {lastAt
                            ? ` · última ${lastFrom === 'seller' ? 'nossa' : 'do comprador'} ${fmtDate(lastAt)}`
                            : ''}
                    </span>
                }
            >
                {hydrateNotice}
                {headline ? (
                    <p className="truncate font-mono text-[11px] text-mono-500 dark:text-mono-400">{headline}</p>
                ) : null}

                {blockedByClaim ? (
                    <Notice tone="amber">
                        <p className="font-semibold text-black dark:text-white">
                            Mensageria bloqueada pelo ML por reclamação aberta
                            {blockingClaimId ? ` (#${blockingClaimId})` : ''}.
                        </p>
                        <p className="text-mono-600 dark:text-mono-300">
                            A resposta ao comprador é pela reclamação, no painel do Mercado Livre.
                        </p>
                        {blockingClaimId ? (
                            <a
                                href={MlClaimMessagesUrl(orderId, blockingClaimId, pack)}
                                target="_blank"
                                rel="noreferrer"
                                className={`${LINK_CLASS} mt-1 font-semibold`}
                            >
                                Responder pela reclamação no ML <ArrowSquareOut size={13} />
                            </a>
                        ) : null}
                    </Notice>
                ) : null}
                {state === 'bloqueada' ? (
                    <Notice tone="neutral">
                        Conversa bloqueada pelo Mercado Livre
                        {conversation?.conversation_substatus ? ` (${conversation.conversation_substatus})` : ''} — não
                        é possível enviar mensagens.
                    </Notice>
                ) : null}

                <ConversationThread conversation={conv} messages={messages} />

                {/* Compositor ABAIXO da lista, junto da mensagem mais recente. */}
                {blocked ? null : state === 'falta_nossa_resposta' ? (
                    <div className="border-l-4 border-l-danger-600 pl-3">
                        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-danger-600 dark:text-danger-400">
                            Sua vez de responder
                        </p>
                        {composer}
                    </div>
                ) : (
                    composer
                )}

                {blockedByClaim ? (
                    <div className="flex flex-col gap-2 border-t border-mono-100 pt-3 dark:border-mono-900">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                            Mensagens da reclamação{blockingClaimId ? ` #${blockingClaimId}` : ''}
                        </p>
                        {blockingRmaId ? (
                            <MediationChat
                                messages={claimThread}
                                loading={claimMessages.loading}
                                attachmentUrl={(filename) => claimAttachmentUrl(blockingRmaId, filename)}
                            />
                        ) : (
                            <p className="text-sm text-mono-500 dark:text-mono-400">
                                Reclamação ainda não espelhada aqui — as mensagens dela estão no painel do ML.
                            </p>
                        )}
                    </div>
                ) : null}
            </Panel>
        </div>
    );
};

// ── Aba Pós-venda (ex-Ocorrências) ───────────────────────────────────────────

/** Cor do processo dominante (primeiro trilho pendente) — como em `Rmas.jsx`. */
function occurrenceAccent(rma, hot) {
    if (hot) {
        return 'border-l-danger-600';
    }
    const tracks = ComputeTracks(rma);
    if (!tracks.ml.na && !tracks.ml.done) {
        return 'border-l-sky-500';
    }
    if (!tracks.fisico.na && !tracks.fisico.done) {
        return 'border-l-amber-500';
    }
    if (!tracks.fabricante.na && !tracks.fabricante.done) {
        return 'border-l-violet-500';
    }
    if (!tracks.financeiro.na && !tracks.financeiro.done) {
        return 'border-l-cyan-600';
    }
    return 'border-l-mono-300 dark:border-l-mono-700';
}

/**
 * Uma LINHA por caso dentro do painel único da aba: "Reclamação #x" /
 * "Mediação #x", "Vez de: …", etapa e motivo, chip "Devolução" quando o caso
 * tem devolução. Expandido = o `RmaDetail` embutido (sem Venda/Garantia/
 * Financeiro/notas próprias) numa área do MESMO painel, com fundo levemente
 * diferente e a borda esquerda do caso — nada de cartão solto. Linha que já
 * nasce expandida (`?caso=` na URL) rola até si uma vez.
 *
 * `onChanged` (F07) vai para o `RmaDetail`: toda ação no caso recarrega o
 * pedido (esta linha, as tags do cabeçalho, o histórico). O caso embutido
 * remonta quando o reembolso ou uma NF muda por fora dele (conciliar, NF de
 * perda, NF-e de devolução na aba Financeiro) — `financeKey`.
 *
 * Rodada 5 (2026-09-29): o painel "Triagem" (`CaseReviewPanel`) fica ACIMA
 * do `RmaDetail` embutido, só no caso expandido — o cabeçalho recolhido
 * continua limpo. Reclamação de ENVIO aberta em outro pedido do pack
 * (`scope: 'pack'`) ganha, sob o cabeçalho, o chip "Caso do pack · aberto no
 * pedido …" com link para o pedido-âncora (o mesmo caso, uma triagem só).
 */
const OccurrenceCase = ({ rma, expanded, onToggle, onClose, onChanged = null, packId = null, backTo = null }) => {
    const rowRef = useRef(null);
    const openedOnMount = useRef(expanded);
    useEffect(() => {
        if (openedOnMount.current) {
            rowRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }, []);
    // Caso ENCERRADO (ML fechou o claim ou nós encerramos: RESOLVIDO/
    // CANCELADO) com prazo já vencido não é urgente (vermelho = urgente):
    // cai no "aberta dd/mm" neutro — mesma regra do `RmaDetail` embutido
    // (`CaseDueInfo`). Encerrado com janela viva mantém o "vence em".
    const caseDue = CaseDueInfo(rma);
    const due = caseDue?.closed ? null : caseDue;
    const hot = Boolean(due?.breached);
    const turn = TurnMeta(OccurrenceTurn(rma));
    const turnClasses = turn ? TONE_CLASSES[turn.tone] || TONE_CLASSES.neutral : null;
    const hasReturn = Boolean(rma.has_return) || (Array.isArray(rma.kinds) && rma.kinds.includes('devolucao'));
    const stage = rma.claim_stage ? MlLabel(ML_CLAIM_STAGE_LABELS, rma.claim_stage) : null;
    const reason = rma.reason_detail || null;
    const accent = occurrenceAccent(rma, hot);
    const financeKey = [rma.refund_status, rma.refund_reconciled_at, rma.nf_loss_at, rma.nf_entry_at]
        .map((v) => v ?? '')
        .join(':');
    const anchorOrderId = rma.scope === 'pack' ? CleanIdentityValue(rma.anchor_order_id) : null;
    return (
        <div ref={rowRef} id={`caso-${rma.id}`}>
            <button
                type="button"
                aria-expanded={expanded}
                onClick={() => onToggle(rma.id)}
                className={`flex w-full flex-col gap-1 border-l-4 px-5 py-2.5 text-left transition-colors ${accent} ${
                    expanded ? 'bg-mono-50 dark:bg-mono-900' : 'hover:bg-mono-50 dark:hover:bg-mono-900/40'
                }`}
            >
                <span className="flex w-full flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-[13px] font-bold text-black dark:text-white">
                        {OccurrenceTitle(rma)}
                    </span>
                    <StatusPill status={rma.status} />
                    {hasReturn ? (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold ${TONE_CLASSES.amber.chip} ${TONE_CLASSES.amber.ring}`}
                        >
                            Devolução
                        </span>
                    ) : null}
                    {turn ? (
                        <span className={`text-[11px] ${turnClasses.text}`}>
                            Vez de: <span className="font-semibold">{turn.label}</span>
                        </span>
                    ) : null}
                    <span className="ml-auto shrink-0 text-[11px]">
                        {due ? (
                            <span className={`font-semibold ${due.tone}`}>{due.label}</span>
                        ) : (
                            <span className="text-mono-400 dark:text-mono-600">
                                aberta {fmtDate(rma.claim_date_created || rma.created_at)}
                            </span>
                        )}
                    </span>
                </span>
                {stage || reason ? (
                    <span className="truncate text-[12px] text-mono-600 dark:text-mono-300">
                        {[stage, reason].filter(Boolean).join(' · ')}
                    </span>
                ) : null}
                {/* Card limpo (pedido do dono, 2026-09-23): sem indicativo de
                    resolução — nada de situação do ML nem chips de desfecho
                    (ganha, sem reembolso…); isso mora dentro do caso. */}
                <span className="min-w-0 truncate text-[11px] text-mono-400 dark:text-mono-500">
                    {[rma.sku, (rma.item_title || '').slice(0, 48), rma.quantity > 1 ? `${rma.quantity} un` : null]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
            </button>
            {anchorOrderId ? (
                <div className={`border-l-4 px-5 pb-2 ${accent} ${expanded ? 'bg-mono-50 dark:bg-mono-900' : ''}`}>
                    <Link
                        to={`/pedidos/${encodeURIComponent(anchorOrderId)}`}
                        state={backTo ? { from: backTo } : undefined}
                        title="Reclamação de envio: vale para o pack inteiro — é o mesmo caso nos pedidos do pack"
                        className="inline-flex items-center gap-1 bg-mono-100 px-2 py-0.5 text-[11px] text-mono-600 ring-1 ring-inset ring-mono-400/20 transition-colors hover:text-black dark:bg-mono-900 dark:text-mono-300 dark:ring-mono-600/30 dark:hover:text-white"
                    >
                        Caso do pack · aberto no pedido <span className="font-mono">#{anchorOrderId}</span>
                    </Link>
                </div>
            ) : null}
            {expanded ? (
                <div className={`border-l-4 bg-mono-50 pb-2 pt-2 dark:bg-mono-900 ${accent}`}>
                    <CaseReviewPanel occ={rma} onChanged={onChanged} />
                    <RmaDetail
                        key={`${rma.id}:${financeKey}`}
                        rmaId={rma.id}
                        embedded
                        packId={packId}
                        onClose={onClose}
                        onChanged={onChanged}
                    />
                </div>
            ) : null}
        </div>
    );
};

/**
 * Casos do pedido num painel só (borda 1px, fundo branco), uma linha por
 * caso separada por `divide-y`; clicar expande o `RmaDetail` embutido dentro
 * do painel. Ao abrir, hidrata as linhas COMPLETAS pelo `/rmas?q=<pedido>`
 * (mesmo objeto que a lista `/rmas` usa) para `SituationLine`/`TrackChips`
 * lerem os mesmos campos de sempre; enquanto isso (ou sem `rmas:read`) vale o
 * subconjunto adaptado do detalhe. Mescla em `MergeOccurrenceRow` (F07): o
 * DETALHE ganha, a linha completa só completa o que falta. A linha completa
 * é buscada de novo sempre que a assinatura `id:status:updated_at` das
 * ocorrências muda (pedido recarregado depois de uma ação ou do Sincronizar).
 *
 * Caso expandido: quando a página passa `onExpandedChange`, o estado é dela
 * (vive na URL como `?caso=<rma_id>`, link compartilhável); senão é local.
 * `onCaseChanged` = recarregar o pedido depois de uma ação no caso embutido.
 * `orderTagKeys` = keys das tags do pedido (etiqueta do caso quando o wire
 * não traz `has_label` — `ApplyOrderLabelHint`).
 * `packCases` (rodada 5, wire `pack_cases`) = reclamações de PEDIDO abertas
 * em OUTROS pedidos do pack: viram o aviso "Outro pedido deste pack tem
 * caso" no topo, com link para o caso lá; sem caso próprio, o aviso ocupa o
 * lugar do "Sem reclamações…". `backTo` preserva o "voltar à lista".
 */
export const PosVendaTab = ({
    orderId,
    packId = null,
    ocorrencias = [],
    canReadRmas = false,
    expandedId = null,
    onExpandedChange = null,
    onCaseChanged = null,
    orderTagKeys = [],
    packCases = [],
    backTo = null,
}) => {
    const [fullRows, setFullRows] = useState(null); // Map id → linha completa
    const [localExpanded, setLocalExpanded] = useState(null);
    const count = ocorrencias.length;
    const occurrenceSignature = OccurrenceSignature(ocorrencias);
    const controlled = typeof onExpandedChange === 'function';
    const expanded = controlled ? expandedId : localExpanded;
    const setExpanded = (id) => {
        if (controlled) {
            onExpandedChange(id);
        } else {
            setLocalExpanded(id);
        }
    };
    const isExpanded = (id) => expanded !== null && expanded !== undefined && String(expanded) === String(id);

    useEffect(() => {
        if (!canReadRmas || !occurrenceSignature) {
            return undefined;
        }
        let alive = true;
        api.getRmas({ q: orderId, limit: 50 })
            .then((data) => {
                if (!alive) {
                    return;
                }
                const rows = Array.isArray(data?.rmas) ? data.rmas : [];
                setFullRows(
                    new Map(rows.filter((row) => row.external_order_id === orderId).map((row) => [row.id, row])),
                );
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [orderId, canReadRmas, occurrenceSignature]);

    const packNotice = <PackCasesNotice packCases={packCases} backTo={backTo} />;

    if (count === 0) {
        return (
            <Panel title="Pós-venda">
                {Array.isArray(packCases) && packCases.length ? (
                    packNotice
                ) : (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Sem reclamações ou mediações neste pedido.
                    </p>
                )}
            </Panel>
        );
    }

    const rows = ApplyOrderLabelHint(
        ocorrencias.map((occ) => MergeOccurrenceRow(occ, fullRows?.get(occ.id))),
        orderTagKeys,
    );

    return (
        <section className="border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-mono-200 px-5 py-2.5 dark:border-mono-800">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Pós-venda · {FormatInt(count)} {count === 1 ? 'caso' : 'casos'}
                </h3>
                {!canReadRmas ? (
                    <span className="text-[11px] text-mono-400 dark:text-mono-500">
                        Abrir o caso exige a permissão rmas:read.
                    </span>
                ) : null}
            </header>
            {Array.isArray(packCases) && packCases.length ? (
                <div className="border-b border-mono-200 px-5 py-2 dark:border-mono-800">{packNotice}</div>
            ) : null}
            <div className="divide-y divide-mono-200 dark:divide-mono-800">
                {rows.map((rma) => (
                    <OccurrenceCase
                        key={rma.id}
                        packId={packId}
                        backTo={backTo}
                        rma={rma}
                        expanded={isExpanded(rma.id)}
                        onToggle={(id) => setExpanded(isExpanded(id) ? null : id)}
                        onClose={() => setExpanded(null)}
                        onChanged={onCaseChanged}
                    />
                ))}
            </div>
        </section>
    );
};

/** Nome da rodada 1 — mantido para quem ainda importar. */
export const OcorrenciasTab = PosVendaTab;

/** `claim_type` do ML → universo do título quando o wire não traz `kind`. */
function packCaseKind(packCase) {
    if (packCase?.kind) {
        return packCase.kind;
    }
    return packCase?.claim_type === 'returns' || packCase?.claim_type === 'return' ? 'devolucao' : 'reclamacao';
}

/**
 * Aviso da Pós-venda (rodada 5): reclamação de PEDIDO aberta em outro pedido
 * do mesmo pack — cada pedido do pack tem a sua; aqui só o link para ela
 * (`/pedidos/<pedido>?tab=posvenda&caso=<rma_id>`). Sem casos → nada.
 */
const PackCasesNotice = ({ packCases = [], backTo = null }) => {
    const list = Array.isArray(packCases) ? packCases.filter((item) => item?.external_order_id) : [];
    if (!list.length) {
        return null;
    }
    return (
        <Notice tone="sky">
            <p className="text-mono-700 dark:text-mono-200">
                {list.length === 1 ? 'Outro pedido deste pack tem caso:' : 'Outros pedidos deste pack têm casos:'}
            </p>
            <ul className="mt-0.5 flex flex-col gap-0.5">
                {list.map((item) => {
                    const query = item.id ? `?tab=posvenda&caso=${encodeURIComponent(item.id)}` : '?tab=posvenda';
                    return (
                        <li key={`${item.external_order_id}:${item.id}`} className="flex flex-wrap items-center gap-2">
                            <Link
                                to={`/pedidos/${encodeURIComponent(item.external_order_id)}${query}`}
                                state={backTo ? { from: backTo } : undefined}
                                className={LINK_CLASS}
                            >
                                {OccurrenceTitle({ ...item, kind: packCaseKind(item) })}{' '}
                                <span className="text-mono-500 dark:text-mono-400">
                                    (pedido <span className="font-mono">#{item.external_order_id}</span>)
                                </span>
                            </Link>
                            {item.status ? <StatusPill status={item.status} /> : null}
                        </li>
                    );
                })}
            </ul>
        </Notice>
    );
};

// ── Aba Financeiro ───────────────────────────────────────────────────────────
// Resumo PURO do pagamento do pedido + marcar como conciliado (pedido do dono,
// 24/09). O laudo completo mora na página Pagamentos, alcançável pelo atalho —
// ela NÃO é "tela antiga": é a tela de detalhe do pagamento.

const PaymentsTable = ({ payments }) => {
    if (!payments?.length) {
        return (
            <p className="text-sm text-mono-500 dark:text-mono-400">Nenhum pagamento registrado para este pedido.</p>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
                <thead>
                    <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                        <th className="py-1 pr-3">Pagamento</th>
                        <th className="pr-3">Status</th>
                        <th className="pr-3">Tipo</th>
                        <th className="pr-3 text-right">Valor</th>
                        <th className="pr-3 text-right">Estornado</th>
                        <th className="pr-3 text-right">Líquido</th>
                        <th className="pr-3">Liberação</th>
                        <th className="pr-3">Aprovado</th>
                        <th>Saúde</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.map((payment) => (
                        <tr key={payment.id} className="border-t border-mono-100 dark:border-mono-900">
                            <td className="py-1.5 pr-3 font-mono text-[12px] font-bold text-black dark:text-white">
                                {payment.id}
                            </td>
                            <td className="pr-3">
                                {payment.status || '—'}
                                {payment.status_detail ? (
                                    <span className="ml-1 text-[11px] text-mono-400 dark:text-mono-500">
                                        {payment.status_detail}
                                    </span>
                                ) : null}
                            </td>
                            <td className="pr-3 text-mono-600 dark:text-mono-300">{payment.payment_type_id || '—'}</td>
                            <td className="pr-3 text-right tabular-nums">{FormatMoney(payment.transaction_amount)}</td>
                            <td className="pr-3 text-right tabular-nums">
                                {FormatMoney(payment.transaction_amount_refunded)}
                            </td>
                            <td className="pr-3 text-right tabular-nums">{FormatMoney(payment.net_received_amount)}</td>
                            <td className="pr-3">
                                {MlLabel(MP_MONEY_RELEASE_LABELS, payment.money_release_status)}
                                {payment.money_release_date ? (
                                    <span className="ml-1 text-[11px] text-mono-400 dark:text-mono-500">
                                        {fmtDate(payment.money_release_date)}
                                    </span>
                                ) : null}
                            </td>
                            <td className="pr-3 text-[12px] text-mono-600 dark:text-mono-300">
                                {fmtDate(payment.date_approved)}
                            </td>
                            <td>
                                <span className="flex flex-wrap items-center gap-1">
                                    <HealthPill status={payment.health} />
                                    {(payment.rules || []).map((rule) => (
                                        <span
                                            key={rule}
                                            className="font-mono text-[10px] text-mono-400 dark:text-mono-500"
                                            title="regra do motor de saúde"
                                        >
                                            {rule}
                                        </span>
                                    ))}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * `financeiro` do detalhe (decisão 10): `{ health: {status, rules}, payments }`
 * só com `payments:read`; `null` = sem permissão. Backend antigo (sem o campo)
 * cai no `payments_health` da rodada 1, que ainda trazia `payments[]`.
 */
export function ResolveFinanceiro(financeiro, paymentsHealth, canReadPayments) {
    if (financeiro !== undefined) {
        return financeiro;
    }
    if (!canReadPayments) {
        return null;
    }
    return {
        health: paymentsHealth ? { status: paymentsHealth.status, rules: paymentsHealth.rules || [] } : null,
        payments: Array.isArray(paymentsHealth?.payments) ? paymentsHealth.payments : [],
    };
}

const REFUND_CHANNEL_LABELS = { ML: 'Pelo ML', POR_FORA: 'Por fora (Pix)' };
const REFUNDED_STATUSES = new Set(['REEMBOLSADO', 'PARCIAL']);
const REFUND_ACTION_CLASS =
    'whitespace-nowrap border px-2.5 py-1 text-[12px] transition-colors disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Data de um marco do reembolso: `undefined` = o backend ainda não manda o
 * campo (mostra "—" e esconde a ação ligada a ele); `null` = ainda não houve.
 */
function refundMilestone(value, pendingText) {
    if (value === undefined) {
        return '—';
    }
    return value ? fmtDate(value) : pendingText;
}

/**
 * "Conciliação dos casos" (F06 da revisão; rodada 5): o financeiro de cada
 * caso saiu do caso embutido e mora aqui, uma linha por caso — encerrado ou
 * em aberto, reembolso, valor, canal e conciliação.
 * "Marcar conciliado/verificado" vale para TODO caso encerrado ainda não
 * conciliado (reclamação, mediação, devolução, cancelamento — com ou sem
 * reembolso; `NeedsCaseReconcile`), mesma chamada do `RmaDetail`
 * (`reconcileRmaRefund`, que não exige reembolso). As NFs do caso (NF-e de
 * devolução e NF de perda, rodada 7) moram no painel "Notas fiscais dos
 * casos" (`CaseFiscalPanel`). Ações só com `rmas:finance:update`; depois de
 * cada uma o pedido é recarregado (`onChanged`). O nº do caso abre a
 * Pós-venda com ele expandido.
 */
const CaseRefundsPanel = ({ ocorrencias = [], canFinance = false, onChanged = null, onOpenCase = null, toast }) => {
    const [busyId, setBusyId] = useState(null);
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const run = async (occ, request, successText, failText) => {
        setBusyId(occ.id);
        try {
            await request();
            toastRef.current?.success(successText);
            await onChanged?.();
        } catch (err) {
            toastRef.current?.error(err.message || failText);
        } finally {
            setBusyId(null);
        }
    };

    const reconcile = (occ) =>
        run(
            occ,
            () => api.reconcileRmaRefund(occ.id),
            'Caso marcado como conciliado/verificado.',
            'Não foi possível marcar o caso como conciliado/verificado.',
        );
    return (
        <Panel
            title="Conciliação dos casos"
            action={
                <span className="text-[11px] text-mono-400 dark:text-mono-500">
                    {canFinance ? 'todo caso encerrado: conciliar/verificar aqui' : 'ações exigem rmas:finance:update'}
                </span>
            }
        >
            <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] text-sm">
                    <thead>
                        <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                            <th className="py-1 pr-3">Caso</th>
                            <th className="pr-3">Reembolso</th>
                            <th className="pr-6 text-right">Valor</th>
                            <th className="pr-3">Canal</th>
                            <th className="pr-3">Conciliado/verificado</th>
                            <th className="text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ocorrencias.map((occ) => {
                            const refunded = REFUNDED_STATUSES.has(occ.refund_status);
                            const closed = IsCaseClosed(occ);
                            // Todo caso encerrado não conciliado (não só reembolsado) — rodada 5 —
                            // e o reembolsado ainda em aberto, como antes.
                            const pendingCheck = NeedsCaseReconcile(occ);
                            const canReconcile = canFinance && pendingCheck;
                            const busy = busyId === occ.id;
                            const channel =
                                occ.refund_channel === undefined
                                    ? '—'
                                    : REFUND_CHANNEL_LABELS[occ.refund_channel] || occ.refund_channel || '—';
                            return (
                                <tr
                                    key={occ.id}
                                    className={`border-t border-mono-100 dark:border-mono-900 ${
                                        pendingCheck ? 'border-l-4 border-l-cyan-600' : ''
                                    }`}
                                >
                                    <td className="py-1.5 pl-2 pr-3">
                                        {onOpenCase ? (
                                            <button
                                                type="button"
                                                onClick={() => onOpenCase(occ.id)}
                                                title="Abrir o caso na Pós-venda"
                                                className="font-mono text-[12px] font-bold text-black underline-offset-2 hover:underline dark:text-white"
                                            >
                                                {OccurrenceTitle(occ)}
                                            </button>
                                        ) : (
                                            <span className="font-mono text-[12px] font-bold text-black dark:text-white">
                                                {OccurrenceTitle(occ)}
                                            </span>
                                        )}
                                        <span
                                            className={`block text-[11px] ${
                                                closed
                                                    ? 'text-mono-500 dark:text-mono-400'
                                                    : 'text-mono-400 dark:text-mono-500'
                                            }`}
                                        >
                                            {closed ? 'encerrado' : 'em aberto'}
                                        </span>
                                    </td>
                                    <td className="pr-3">
                                        {REFUND_LABELS[occ.refund_status] || occ.refund_status || '—'}
                                    </td>
                                    <td className="pr-6 text-right tabular-nums">
                                        {occ.refund_amount === null || occ.refund_amount === undefined
                                            ? '—'
                                            : FormatMoney(occ.refund_amount)}
                                    </td>
                                    <td className="pr-3 text-mono-600 dark:text-mono-300">{channel}</td>
                                    <td
                                        className="pr-3 text-[12px] text-mono-600 dark:text-mono-300"
                                        title={closed || refunded ? undefined : 'Só depois que o caso encerrar'}
                                    >
                                        {closed || refunded
                                            ? refundMilestone(occ.refund_reconciled_at, 'ainda não')
                                            : '—'}
                                    </td>
                                    <td className="text-right">
                                        <span className="inline-flex flex-wrap justify-end gap-1.5">
                                            {canReconcile ? (
                                                <button
                                                    type="button"
                                                    onClick={() => reconcile(occ)}
                                                    disabled={busyId !== null}
                                                    className={`${REFUND_ACTION_CLASS} border-cyan-700 font-semibold text-cyan-800 hover:bg-cyan-50 dark:border-cyan-500 dark:text-cyan-300 dark:hover:bg-cyan-950`}
                                                >
                                                    {busy ? 'Marcando…' : 'Marcar conciliado/verificado'}
                                                </button>
                                            ) : null}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
};

/**
 * O caso tem NF para o Financeiro olhar (rodada 7)? Devolução vindo para nós
 * (NF-e de devolução — do ML ou nossa), NF-e do ML já emitida, NF já
 * marcada ou NF de perda cabível ("chegou errado"; `lossTagged` = o pedido
 * tem a tag `nf_perda_para_emitir`, para o wire sem `inspection_issue`).
 */
export function CaseFiscalApplies(occ, { lossTagged = false } = {}) {
    if (!occ) {
        return false;
    }
    return (
        IsReturnToUs(occ) ||
        HasMlReturnInvoice(occ) ||
        Boolean(occ.nf_entry_at) ||
        Boolean(occ.nf_loss_at) ||
        NeedsLossNf(occ, { lossTagged })
    );
}

/**
 * "Notas fiscais dos casos" (rodada 7 — spec-fiscal + spec-nf-xml): uma linha
 * por caso com NF a tratar, duas colunas —
 *   NF-e de devolução: "emitida pelo ML nº X" + DANFE; ou a nossa ("nº X ·
 *     chave … · baixar XML"); ou pendente com "Marcar NF-e de devolução
 *     emitida" (devolução para nós sem NF-e do ML — `NeedsReturnNfe`);
 *   NF de perda: só quando a devolução chegou ERRADA (inspeção com problema
 *     — `NeedsLossNf`) ou já marcada; "Marcar NF de perda emitida".
 * Marcar = subir o XML da NF-e (`CaseFiscalAction`), só com
 * `rmas:finance:update`; depois o pedido recarrega (`onChanged`). NF de
 * devolução ao FORNECEDOR não entra aqui (é da tela de RMAs). Sem caso com
 * NF → não renderiza.
 */
const CaseFiscalPanel = ({
    ocorrencias = [],
    canFinance = false,
    onChanged = null,
    onOpenCase = null,
    toast = null,
    orderTagKeys = [],
}) => {
    const lossTagged = orderTagKeys.includes('nf_perda_para_emitir');
    const rows = ocorrencias.filter((occ) => canFinance || CaseFiscalApplies(occ, { lossTagged }));
    if (!rows.length) {
        return null;
    }
    const columnLabel = 'text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600';
    return (
        <Panel
            title="Notas fiscais dos casos"
            action={
                <span className="text-[11px] text-mono-400 dark:text-mono-500">
                    {canFinance ? 'emitir no Bling e vincular ao caso' : 'vínculo de notas pelo financeiro'}
                </span>
            }
        >
            <div className="flex flex-col divide-y divide-mono-100 dark:divide-mono-900">
                {rows.map((occ) => {
                    const nfePending = NeedsReturnNfe(occ);
                    const lossPending = NeedsLossNf(occ, { lossTagged });
                    const entryShown = IsReturnToUs(occ) || HasMlReturnInvoice(occ) || Boolean(occ.nf_entry_at);
                    // Devolução cancelada/expirada (o comprador não postou) e
                    // sem NF nenhuma: não há NF-e de devolução a emitir — texto
                    // neutro, sem pendência (como a tag nfe_devolucao_para_emitir).
                    const entrySkipped = IsReturnSkipped(occ) && !HasMlReturnInvoice(occ) && !occ.nf_entry_at;
                    const lossShown = lossPending || Boolean(occ.nf_loss_at);
                    return (
                        <div
                            key={occ.id}
                            className={`grid grid-cols-1 gap-x-6 gap-y-2 border-l-4 py-2.5 pl-3 md:grid-cols-[11rem_minmax(0,1fr)_minmax(0,1fr)] ${
                                nfePending || lossPending ? 'border-l-cyan-600' : 'border-l-transparent'
                            }`}
                        >
                            <div className="min-w-0">
                                {onOpenCase ? (
                                    <button
                                        type="button"
                                        onClick={() => onOpenCase(occ.id)}
                                        title="Abrir o caso na Pós-venda"
                                        className="font-mono text-[12px] font-bold text-black underline-offset-2 hover:underline dark:text-white"
                                    >
                                        {OccurrenceTitle(occ)}
                                    </button>
                                ) : (
                                    <span className="font-mono text-[12px] font-bold text-black dark:text-white">
                                        {OccurrenceTitle(occ)}
                                    </span>
                                )}
                                <span className="block text-[11px] text-mono-400 dark:text-mono-500">
                                    {IsCaseClosed(occ) ? 'encerrado' : 'em aberto'}
                                    {occ.is_full ? ' · venda Full' : ''}
                                </span>
                            </div>
                            <div className="min-w-0">
                                <p className={columnLabel}>NF-e de devolução</p>
                                {entryShown && entrySkipped ? (
                                    <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                        — devolução não aconteceu (cancelada/expirada)
                                    </span>
                                ) : entryShown ? (
                                    <CaseFiscalAction
                                        occ={occ}
                                        type="ENTRADA"
                                        needed={nfePending}
                                        canFinance={canFinance}
                                        toast={toast}
                                        onMarked={onChanged}
                                    />
                                ) : (
                                    <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                        — sem devolução para o nosso galpão
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className={columnLabel}>NF de perda</p>
                                {lossShown ? (
                                    <CaseFiscalAction
                                        occ={occ}
                                        type="PERDA"
                                        needed={lossPending}
                                        canFinance={canFinance}
                                        toast={toast}
                                        onMarked={onChanged}
                                    />
                                ) : (
                                    <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                        — sem NF de perda indicada
                                    </span>
                                )}
                                <CaseLossRequest
                                    occ={occ}
                                    canFinance={canFinance}
                                    onChanged={onChanged}
                                    toast={toast}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
};

/**
 * Conciliação por pedido: saúde + pagamentos (do detalhe) + laudo completo do
 * motor (`/payments/order/:id/analysis`, sob demanda, cacheado enquanto a aba
 * viver) com os mesmos componentes de `pages/PaymentOrderDetail.jsx`.
 *
 * Abaixo da conciliação, "Conciliação dos casos" (F06) e "Notas fiscais dos
 * casos" (rodada 7): visíveis com `payments:read` OU `rmas:finance:update`
 * (quem opera o reembolso e as NFs), ações só com `rmas:finance:update`.
 * `orderTagKeys` = keys das tags do pedido (NF de perda quando o wire não
 * traz `inspection_issue`).
 *
 * Rodada 5 (2026-09-29), ACIMA da conciliação e separados dela: "Status do
 * financeiro" do pedido (`financeReview` = wire `finance_review`; Não visto /
 * Visto / Pendência, `FinanceReviewPanel`) e "Apoio solicitado ao
 * financeiro" (casos com apoio em aberto — `FinanceSupportCasesPanel`, a
 * mesma ação da triagem). `onFinanceReviewSaved(review)` = a página
 * atualiza o pedido.
 */
export const FinanceiroTab = ({
    orderId,
    financeiro,
    paymentsHealth = null,
    canReadPayments = false,
    ocorrencias = [],
    canFinance = false,
    onCaseChanged = null,
    onOpenCase = null,
    toast = null,
    orderTagKeys = [],
    financeReview = null,
    onFinanceReviewSaved = null,
}) => {
    const resolved = ResolveFinanceiro(financeiro, paymentsHealth, canReadPayments);
    const cases = Array.isArray(ocorrencias) ? ocorrencias : [];
    // Conciliação do PEDIDO (24/09): marca/desmarca sem sair da aba; o retorno
    // do backend é a fonte, não um palpite local.
    const [reconciliation, setReconciliation] = useState(financeiro?.reconciliation || null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setReconciliation(financeiro?.reconciliation || null);
    }, [financeiro]);
    const toggleReconciled = async () => {
        const next = !reconciliation?.reconciled;
        setSaving(true);
        try {
            const data = await api.setPedidoConciliacao(orderId, { reconciled: next });
            setReconciliation(data?.reconciliation || null);
            toast?.success?.(next ? 'Pedido marcado como conciliado.' : 'Conciliação desmarcada.');
        } catch (error) {
            toast?.error?.(error.message || 'Não foi possível salvar a conciliação.');
        } finally {
            setSaving(false);
        }
    };
    const fiscalPanel =
        cases.length && (canReadPayments || canFinance) ? (
            <CaseFiscalPanel
                ocorrencias={cases}
                canFinance={canFinance}
                onChanged={onCaseChanged}
                onOpenCase={onOpenCase}
                toast={toast}
                orderTagKeys={orderTagKeys}
            />
        ) : null;
    const refundsPanel =
        cases.length && (canReadPayments || canFinance) ? (
            <CaseRefundsPanel
                ocorrencias={cases}
                canFinance={canFinance}
                onChanged={onCaseChanged}
                onOpenCase={onOpenCase}
                toast={toast}
            />
        ) : null;
    const reviewPanels = (
        <>
            <FinanceReviewPanel
                orderId={orderId}
                review={financeReview}
                canEdit={canFinance}
                onSaved={onFinanceReviewSaved}
            />
            <FinanceSupportCasesPanel
                ocorrencias={cases}
                canFinance={canFinance}
                onChanged={onCaseChanged}
                onOpenCase={onOpenCase}
            />
        </>
    );

    if (resolved === null) {
        return (
            <div className="flex flex-col gap-4">
                {reviewPanels}
                <Panel title="Financeiro">
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Sem permissão de pagamentos (payments:read).
                    </p>
                </Panel>
                {refundsPanel}
                {fiscalPanel}
            </div>
        );
    }

    const health = resolved?.health || null;
    const rules = Array.isArray(health?.rules) ? health.rules : [];
    const payments = Array.isArray(resolved?.payments) ? resolved.payments : [];

    return (
        <div className="flex flex-col gap-4">
            {reviewPanels}
            <Panel
                title={
                    <>
                        Conciliação do pedido <OriginBadge origin="ml" />
                    </>
                }
                action={
                    <span className="flex items-center gap-3">
                        {reconciliation?.reconciled ? (
                            <span
                                className="text-[11px] text-mono-500 dark:text-mono-400"
                                title={reconciliation.reconciled_at || ''}
                            >
                                conciliado
                                {reconciliation.reconciled_by_name ? ` por ${reconciliation.reconciled_by_name}` : ''}
                            </span>
                        ) : null}
                        {canFinance ? (
                            <button
                                type="button"
                                onClick={toggleReconciled}
                                disabled={saving}
                                className={REFUND_ACTION_CLASS}
                            >
                                {reconciliation?.reconciled ? 'Desmarcar conciliação' : 'Marcar como conciliado'}
                            </button>
                        ) : null}
                    </span>
                }
            >
                {health ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <HealthPill status={health.status} size="md" />
                        {rules.length ? (
                            rules.map((rule) => (
                                <span
                                    key={rule}
                                    className="bg-mono-100 px-1.5 py-0.5 font-mono text-[11px] text-mono-600 dark:bg-mono-900 dark:text-mono-300"
                                    title="regra apontada pelo motor de saúde (orderHealth)"
                                >
                                    {rule}
                                </span>
                            ))
                        ) : (
                            <span className="text-[11px] text-mono-400 dark:text-mono-500">
                                nenhuma regra apontada na lista
                            </span>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Sem pagamentos espelhados para este pedido — nada a conciliar ainda.
                    </p>
                )}
                <PaymentsTable payments={payments} />
            </Panel>

            {refundsPanel}

            {fiscalPanel}

            {canReadPayments ? <PaymentOrderReport key={orderId} orderId={orderId} embedded /> : null}
        </div>
    );
};

// ── Aba JSON raw (só `developer`) ────────────────────────────────────────────
// Ferramenta PERPÉTUA do dev: o JSON de cada fonte que alimenta o detalhe
// (`GET /pedidos/:id/payloads`), com o endpoint que o originou e um botão
// "Request" que refaz a consulta AGORA pelo backend — é assim que se vê a
// diferença entre o que está no banco e o que a API devolveria hoje.

const PAYLOAD_SOURCES = [
    { key: 'sale_raw', label: 'Venda' },
    { key: 'shipment_raw', label: 'Envio' },
    { key: 'claims_raw', label: 'Reclamações' },
    { key: 'conversation_ml', label: 'Conversa ML' },
    { key: 'payments_raw', label: 'Pagamentos' },
];

function payloadSize(value) {
    if (value === null || value === undefined) {
        return 'vazio';
    }
    if (Array.isArray(value)) {
        return `${FormatInt(value.length)} ${value.length === 1 ? 'item' : 'itens'}`;
    }
    return 'objeto';
}

const PAYLOAD_BTN_CLASS =
    'border border-mono-300 px-2 py-0.5 text-[11px] text-mono-600 transition-colors hover:border-black hover:text-black disabled:cursor-not-allowed disabled:opacity-50 dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white';

/** Selo da origem: Local = nosso banco; Live = consultado agora no provedor. */
const OriginTag = ({ live }) => (
    <span
        className="font-sans text-[10px] uppercase tracking-wider text-mono-400 dark:text-mono-600"
        title={live ? 'consultado no provedor ao abrir a aba' : 'gravado por worker; aqui é leitura do nosso banco'}
    >
        {live ? 'Live' : 'Local'}
    </span>
);

/**
 * Um bloco da aba JSON raw. `source` traz o endpoint de origem; quando ele é
 * consultável, o botão "Request" refaz a chamada pelo backend e mostra a
 * resposta de agora embaixo do JSON do banco, para comparar.
 */
const PayloadBlock = ({ label, value, source = null, onRequest = null, nested = false }) => {
    const [open, setOpen] = useState(false); // fechados por padrão (pedido do dono)
    const [copied, setCopied] = useState(false);
    const [live, setLive] = useState(null); // { http_status, fetched_at, body }
    const [requesting, setRequesting] = useState(false);
    const [requestError, setRequestError] = useState('');
    const json = value === undefined ? 'undefined' : JSON.stringify(value, null, 2);
    const Caret = open ? CaretDown : CaretRight;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(json);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    const request = async () => {
        setRequesting(true);
        setRequestError('');
        try {
            const data = await onRequest();
            setLive(data);
            setOpen(true);
        } catch (error) {
            setRequestError(error.message || 'Não foi possível consultar o endpoint.');
        } finally {
            setRequesting(false);
        }
    };

    return (
        <div
            className={`border bg-white dark:bg-mono-950 ${
                nested ? 'border-mono-100 dark:border-mono-900' : 'border-mono-200 dark:border-mono-800'
            }`}
        >
            <div className="flex items-center gap-2 px-3 py-2">
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpen((current) => !current)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                    <Caret size={13} className="shrink-0 text-mono-400" />
                    <span
                        className={`truncate ${
                            nested
                                ? 'font-mono text-[11px] text-mono-600 dark:text-mono-300'
                                : 'text-[11px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400'
                        }`}
                    >
                        {label}
                    </span>
                    <span className="text-[11px] text-mono-400 dark:text-mono-600">{payloadSize(value)}</span>
                </button>
                {onRequest ? (
                    <button type="button" onClick={request} disabled={requesting} className={PAYLOAD_BTN_CLASS}>
                        {requesting ? 'Consultando…' : 'Request'}
                    </button>
                ) : null}
                <button type="button" onClick={copy} className={PAYLOAD_BTN_CLASS}>
                    {copied ? 'Copiado' : 'Copiar'}
                </button>
            </div>
            {source?.endpoint ? (
                <p className="flex items-center gap-2 border-t border-mono-100 px-3 py-1.5 font-mono text-[10px] leading-snug text-mono-500 dark:border-mono-900 dark:text-mono-400">
                    <span className="min-w-0 flex-1 break-all">{source.endpoint}</span>
                    <OriginTag live={source.live} />
                </p>
            ) : null}
            {requestError ? (
                <p className="border-t border-mono-100 px-3 py-1.5 text-[11px] text-danger-600 dark:border-mono-900 dark:text-danger-400">
                    {requestError}
                </p>
            ) : null}
            {open ? (
                <pre className="max-h-[32rem] overflow-auto border-t border-mono-100 bg-mono-50 p-3 font-mono text-[11px] leading-snug text-mono-800 dark:border-mono-900 dark:bg-black/40 dark:text-mono-200">
                    {json}
                </pre>
            ) : null}
            {open && live ? (
                <>
                    <p className="flex items-center gap-2 border-t border-mono-100 bg-mono-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-mono-500 dark:border-mono-900 dark:bg-black/40 dark:text-mono-400">
                        resposta agora · HTTP {live.http_status ?? '—'} · {fmtDate(live.fetched_at)}
                    </p>
                    <pre className="max-h-[32rem] overflow-auto border-t border-mono-100 bg-mono-50 p-3 font-mono text-[11px] leading-snug text-mono-800 dark:border-mono-900 dark:bg-black/40 dark:text-mono-200">
                        {JSON.stringify(live.body, null, 2)}
                    </pre>
                </>
            ) : null}
        </div>
    );
};

export const PayloadsTab = ({ orderId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        api.getPedidoPayloads(orderId)
            .then((payloads) => {
                if (alive) {
                    setData(payloads || {});
                }
            })
            .catch((loadError) => {
                if (alive) {
                    setError(loadError.message || 'JSON raw indisponível');
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [orderId]);

    if (loading) {
        return <LoadingSpinner className="py-12" />;
    }
    if (error) {
        return (
            <Panel title="JSON raw">
                <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
            </Panel>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <p className="text-[11px] text-mono-400 dark:text-mono-500">
                JSON de cada fonte deste detalhe. <strong>Local</strong> = como está no nosso banco;{' '}
                <strong>Live</strong> = consultado agora no provedor. O botão <strong>Request</strong> refaz a consulta
                naquele endpoint, pelo backend, e mostra a resposta ao lado — serve para comparar com o banco.
            </p>
            {PAYLOAD_SOURCES.map((source) => {
                const info = data?.payload_sources?.[source.key];
                const value = data?.[source.key];
                const items = info?.items || [];
                return (
                    <div key={source.key} className="flex flex-col gap-1">
                        <PayloadBlock
                            label={source.label}
                            value={value}
                            source={info}
                            onRequest={
                                info?.requestable
                                    ? () => api.requestPedidoPayload(orderId, { source: source.key })
                                    : null
                            }
                        />
                        {/* Fonte que é lista (reclamações, pagamentos): um bloco por item,
                            cada um com o endpoint do próprio id e o seu "Request". */}
                        {items.length ? (
                            <div className="flex flex-col gap-1 pl-4">
                                {items.map((item, index) => (
                                    <PayloadBlock
                                        key={item.id}
                                        nested
                                        label={`${source.label} · ${item.id}`}
                                        value={Array.isArray(value) ? value[index] : undefined}
                                        source={{ endpoint: item.endpoint, live: false }}
                                        onRequest={() =>
                                            api.requestPedidoPayload(orderId, {
                                                source: source.key,
                                                item_id: item.id,
                                            })
                                        }
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};
